import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import './Navigation.css';

const Navigation = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isForArtistsOpen, setIsForArtistsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsForArtistsOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsForArtistsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Music', path: '/releases' },
    { name: 'Artists', path: '/artists' },
    { name: 'Events', path: '/events' },
    { name: 'About', path: '/about' }
  ];

  const forArtistsLinks = [
    { name: 'Why AMR', path: '/for-artists' },
    { name: 'Artist Support', path: '/artist-support' },
    { name: 'Submit Demo', path: '/submit-demo' },
    { name: 'Artist Login', path: '/artist/login' }
  ];

  const isForArtistsActive = forArtistsLinks.some(link => location.pathname === link.path);

  const handleLinkClick = (path) => {
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      if (route === '' || route === '/') {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  return (
    <nav className={`navigation ${isSticky ? 'sticky' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img 
            src="/amr-logo.png" 
            alt="Anyone Music Records" 
            className="logo-image"
          />
        </Link>

        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {navLinks.map((link) => (
            <li key={link.name} className="nav-item">
              <Link 
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                onClick={() => handleLinkClick(link.path)}
              >
                {link.name}
              </Link>
            </li>
          ))}
          
          {/* For Artists Dropdown */}
          <li className="nav-item nav-dropdown" ref={dropdownRef}>
            <button 
              className={`nav-link nav-link-highlight ${isForArtistsActive ? 'active' : ''}`}
              onClick={() => setIsForArtistsOpen(!isForArtistsOpen)}
              aria-expanded={isForArtistsOpen}
            >
              For Artists
              <ChevronDown size={14} className={`dropdown-icon ${isForArtistsOpen ? 'open' : ''}`} />
            </button>
            <ul className={`dropdown-menu ${isForArtistsOpen ? 'open' : ''}`}>
              {forArtistsLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className={`dropdown-link ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => setIsForArtistsOpen(false)}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          
          <li className="nav-item">
            <Link 
              to="/contact"
              className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
            >
              Contact
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
